// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Download Service — resolves a dataset's downloadable assets for the
 * zip download (📦 on Browse cards, `zipDownloadService.ts`).
 *
 * `resolveAssets(dataset)` is the shared asset-resolution surface: it
 * returns a typed `ResolvedAsset[]` describing the primary + auxiliary
 * downloads. The source-of-truth field on each asset is what the zip
 * dialog renders ("from AYNI's NOAA library", "from the publisher") so
 * the user knows what's in the archive.
 *
 * AYNI (2026-10-07): every NOAA file is one object in AYNI's library
 * (`catalog.pachamama-studios.stream`), downloaded as it is. TerraViz's
 * Vimeo MP4 pick through the Zyra Project's proxy and the CloudFront
 * `_4096` / `_2048` HEAD probes went with the S3 catalog.
 *
 * AYNI: TerraViz's desktop (Tauri) offline downloads — the Rust
 * download manager and its commands — were removed with the desktop
 * app (2026-10-06); only the web zip path remains.
 */

import type { Dataset } from '../types'
import { getApiOrigin } from './catalogSource'
import { isLibraryMediaUrl } from './lessonLibrary'
import { logger } from '../utils/logger'

// Hostnames whose URLs we trust as "publisher original" — the
// Cloudflare Pages origin and any subdomain of an operated domain
// (R2 public buckets bound to custom domains, image transformations
// served from the main origin, etc.). Subdomains match by suffix. A
// URL served from any other host falls back to `external` rather
// than being mislabelled as a publisher source.
//
// AYNI: the stream host's domain (`pachamama-studios.stream`); this
// node's own host is added dynamically by `publisherHosts()` below.
// The NOAA library's host is classified first, as `library`.
//
// Intentionally NOT including the bare `r2.dev` apex — a `*.r2.dev`
// suffix match would classify every third-party R2 public bucket as
// our publisher source. Production R2 assets are served via custom
// domains under the operated domain; the default `*.r2.dev` URL
// shouldn't appear in our manifests, and if it does it should
// classify as `external` until someone explicitly adds the specific
// bucket subdomain here.
const PUBLISHER_HOSTS = [
  'pachamama-studios.stream',
]

// Loopback / pseudo-hosts that must never be trusted as a publisher
// suffix: they never appear in a manifest, and a local-dev
// `VITE_API_ORIGIN=http://localhost:...` would otherwise classify
// every loopback URL as a publisher source.
function isLoopbackHost(host: string): boolean {
  return !host || host === 'localhost' || host.endsWith('.localhost')
}

// The set of trusted publisher hosts for the running node: the
// static upstream domain(s) above plus this node's own host. The
// own-host is derived from the configured API origin
// (`VITE_API_ORIGIN`) and the live page origin — whichever
// resolves. `localhost` pseudo-hosts are skipped
// (see `isLoopbackHost`): they never appear in a manifest and only
// add noise.
function publisherHosts(): string[] {
  const hosts = [...PUBLISHER_HOSTS]
  try {
    const apiHost = new URL(getApiOrigin()).hostname.toLowerCase()
    if (!isLoopbackHost(apiHost)) hosts.push(apiHost)
  } catch {
    // getApiOrigin always returns a valid origin, but guard anyway.
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const pageHost = window.location.hostname.toLowerCase()
    if (!isLoopbackHost(pageHost)) hosts.push(pageHost)
  }
  return hosts
}

// --- Types ---

/** Asset role within a dataset's downloadable bundle. Drives both
 * the checkbox labels in the zip dialog and the filename layout
 * inside the resulting archive. */
export type AssetKind =
  | 'primary'
  | 'legend'
  | 'caption'
  | 'thumbnail'
  | 'colorTable'
  | 'frame'

/** Provenance of a resolved asset URL. The zip dialog surfaces this
 * so users know whether the archive holds a file of AYNI's NOAA
 * library, the publisher's own upload, or something hosted elsewhere.
 *
 * Derived by inspecting the URL's hostname: the library host, then
 * `PUBLISHER_HOSTS` and this node's host. Anything else falls through
 * to `external`. */
export type SourceOfTruth = 'library' | 'publisher' | 'external'

/** One downloadable file the zip path will fetch. */
export interface ResolvedAsset {
  kind: AssetKind
  url: string
  /** Suggested archive filename. Stable across reruns — the zip
   * service uses these verbatim as zip-entry names. */
  filename: string
  /** Known size in bytes when the upstream manifest exposes it
   * (video manifests do; image variants don't until a HEAD probe).
   * Consumers that need an exact total should HEAD any asset whose
   * `sizeBytes` is undefined. */
  sizeBytes?: number
  sourceOfTruth: SourceOfTruth
}

// --- Helpers ---

/**
 * True if `url` is an absolute http(s) URL — i.e. something the zip
 * path can fetch as-is. Catches relative paths (`/api/v1/...`),
 * raw `r2:` / `stream:` / `vimeo:` data_ref schemes leaked through
 * the catalog serializer, and empty strings.
 */
function isHttpUrl(url: string | null | undefined): url is string {
  if (!url) return false
  return /^https?:\/\//i.test(url)
}

/**
 * Pull the file extension off a URL (`.mp4`, `.jpg`, `.png`), tolerant
 * of query strings. Falls back to `defaultExt` if nothing matches, and
 * for a library object (`<sha>.<sha>`), whose "extension" is its hash;
 * the zip names those by the type the bucket serves (`extForContentType`).
 */
function extFromUrl(url: string, defaultExt: string): string {
  const match = url.match(/(\.\w+)(\?|#|$)/)
  if (!match || /^\.[0-9a-f]{32,}$/i.test(match[1])) return defaultExt
  return match[1]
}

const EXT_FOR_TYPE: Readonly<Record<string, string>> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
  'video/mp4': '.mp4',
  'text/plain': '.srt',
  'application/x-subrip': '.srt',
}

/**
 * The file extension for a served Content-Type, or null for a type with
 * no known extension. AYNI: a library object's URL has none, so the zip
 * names it after the type the bucket answers with.
 */
export function extForContentType(contentType: string | null | undefined): string | null {
  const type = contentType?.split(';')[0].trim().toLowerCase()
  return (type && EXT_FOR_TYPE[type]) || null
}

/** `filename` with its extension made to match `contentType` (unchanged when the type is unknown). */
export function filenameForContentType(filename: string, contentType: string | null | undefined): string {
  const ext = extForContentType(contentType)
  if (!ext) return filename
  return filename.replace(/(\.\w+)?$/, ext)
}

/**
 * Classify a URL by its hostname so the UI can render an honest
 * source-of-truth note in the zip dialog. The check parses via the
 * URL constructor (subdomain-safe — never substring-matches) and
 * matches the library host, then the explicit `PUBLISHER_HOSTS` list
 * and this node's host.
 *
 * Errors (malformed URL) and any host outside those buckets fall
 * through to `external`, which the dialog labels generically.
 */
export function classifySourceOfTruth(url: string): SourceOfTruth {
  let host: string
  try {
    host = new URL(url).hostname.toLowerCase()
  } catch {
    return 'external'
  }
  if (isLibraryMediaUrl(url)) return 'library'
  for (const publisher of publisherHosts()) {
    if (host === publisher || host.endsWith(`.${publisher}`)) return 'publisher'
  }
  return 'external'
}

/**
 * Resolve only the auxiliary assets attached to a dataset row —
 * legend / caption / thumbnail / color table. Pure HTTP-URL
 * filtering: refs that aren't absolute http(s) are silently dropped
 * (logged at warn level), since the catalog serializer occasionally
 * passes raw `r2:` / `stream:` / `vimeo:` URIs through verbatim.
 *
 * Extracted from `resolveAssets()` so callers that don't want the
 * primary (notably the web zip path's frame-mode flow, where the
 * frame bundle replaces the rendered primary and the video manifest
 * would throw HLS-only) can resolve auxiliaries directly without
 * tripping a primary-asset resolution error.
 */
function resolveAuxiliaryAssets(dataset: Dataset): ResolvedAsset[] {
  const assets: ResolvedAsset[] = []

  // The catalog serializer currently passes `thumbnail_ref` /
  // `legend_ref` / `caption_ref` through verbatim, so they may arrive
  // as raw `r2:` / `stream:` / `vimeo:` URIs rather than absolute
  // https URLs. Filter to http(s) only — the downstream fetch
  // refuses anything else, and a missing thumbnail is much better
  // UX than a failed download.
  if (isHttpUrl(dataset.legendLink)) {
    const ext = extFromUrl(dataset.legendLink, '.png')
    assets.push({
      kind: 'legend',
      url: dataset.legendLink,
      filename: `legend${ext}`,
      sourceOfTruth: classifySourceOfTruth(dataset.legendLink),
    })
  } else if (dataset.legendLink) {
    logger.warn('[Download] Skipping non-HTTP legend ref:', dataset.legendLink)
  }

  if (isHttpUrl(dataset.closedCaptionLink)) {
    assets.push({
      kind: 'caption',
      url: dataset.closedCaptionLink,
      filename: 'captions.srt',
      sourceOfTruth: classifySourceOfTruth(dataset.closedCaptionLink),
    })
  } else if (dataset.closedCaptionLink) {
    logger.warn('[Download] Skipping non-HTTP caption ref:', dataset.closedCaptionLink)
  }

  if (isHttpUrl(dataset.thumbnailLink)) {
    const ext = extFromUrl(dataset.thumbnailLink, '.jpg')
    assets.push({
      kind: 'thumbnail',
      url: dataset.thumbnailLink,
      filename: `thumbnail${ext}`,
      sourceOfTruth: classifySourceOfTruth(dataset.thumbnailLink),
    })
  } else if (dataset.thumbnailLink) {
    logger.warn('[Download] Skipping non-HTTP thumbnail ref:', dataset.thumbnailLink)
  }

  if (isHttpUrl(dataset.colorTableLink)) {
    const ext = extFromUrl(dataset.colorTableLink, '.png')
    assets.push({
      kind: 'colorTable',
      url: dataset.colorTableLink,
      filename: `color-table${ext}`,
      sourceOfTruth: classifySourceOfTruth(dataset.colorTableLink),
    })
  } else if (dataset.colorTableLink) {
    logger.warn('[Download] Skipping non-HTTP color-table ref:', dataset.colorTableLink)
  }

  return assets
}

/**
 * Resolve every downloadable asset for `dataset`, in primary-first
 * order. The primary entry is always index 0; auxiliary entries
 * (legend / caption / thumbnail) follow when present.
 *
 * Throws on a primary-asset failure (HLS-only, no usable image
 * variants); auxiliary failures are silently filtered (we'd rather
 * surface a partial archive than reject the whole download because
 * a thumbnail 404s).
 *
 * The web zip path consumes this — the zip dialog then filters the result by user-selected
 * `AssetKind`s, attaches a `manifest.json`, and packages.
 */
export async function resolveAssets(
  dataset: Dataset,
  opts: { signal?: AbortSignal } = {},
): Promise<ResolvedAsset[]> {
  // The primary is the dataset's own file, downloaded as it is: one MP4
  // or one picture. The dialog probes its size (HEAD). Nothing here waits
  // on the network any more, but a dialog closed before this ran still
  // gets its AbortError.
  opts.signal?.throwIfAborted()
  const isVideo = dataset.format.startsWith('video/')
  if (!isHttpUrl(dataset.dataLink)) throw new Error(`No downloadable file for ${dataset.id}`)
  const ext = extFromUrl(dataset.dataLink, extForContentType(dataset.format) ?? (isVideo ? '.mp4' : '.png'))
  const assets: ResolvedAsset[] = [{
    kind: 'primary',
    url: dataset.dataLink,
    filename: `${isVideo ? 'video' : 'image'}${ext}`,
    sourceOfTruth: classifySourceOfTruth(dataset.dataLink),
  }]
  assets.push(...resolveAuxiliaryAssets(dataset))
  return assets
}

/**
 * Pure resolver for auxiliary assets only — public surface for the
 * web zip path's frame-mode flow. Same semantics as the auxiliary
 * portion of `resolveAssets()` but without attempting to resolve a
 * video primary that would throw HLS-only for post-transcode
 * publisher datasets. The frames are the canonical downloadable
 * primary for those rows; the caller pairs this with
 * `expandFrameAssets(dataset)`.
 */
export function resolveAuxiliaryAssetsOnly(dataset: Dataset): ResolvedAsset[] {
  return resolveAuxiliaryAssets(dataset)
}

/**
 * Pure expansion of a frames-mode dataset's `frames.urlTemplate` into
 * one ResolvedAsset per frame. Returns `[]` when the dataset has no
 * `frames` envelope. The zip service uses this when the user opts to
 * download source frames in addition to (or instead of) the rendered
 * primary; the cap-at-1.5-GB enforcement in the dialog gates the
 * actual download.
 *
 * `{index}` in `urlTemplate` is substituted with a zero-padded 5-digit
 * frame index, mirroring the convention the publisher portal /
 * `/api/v1/datasets/{id}/frames` endpoint emits.
 */
export function expandFrameAssets(dataset: Dataset): ResolvedAsset[] {
  if (!dataset.frames) return []
  const { count, urlTemplate } = dataset.frames
  // Same fail-closed guard `resolveFrameQuery` uses for chat-driven
  // frame loads (`src/utils/frames.ts:60`): non-integer / non-
  // positive `count` means the row is corrupt or mid-ingest and the
  // for-loop bound below would silently generate bogus indices
  // (NaN ⇒ no iterations, Infinity ⇒ unbounded loop). Drop the
  // bundle rather than emitting unbounded or off-by-many URLs.
  if (!Number.isInteger(count) || count <= 0) return []
  if (!urlTemplate) return []
  const assets: ResolvedAsset[] = []
  for (let i = 0; i < count; i++) {
    const padded = String(i).padStart(5, '0')
    const url = urlTemplate.replace(/\{index\}/g, padded)
    if (!isHttpUrl(url)) continue
    // Filename: `frame_{NNNNN}{ext}` — match the publisher portal's
    // server-rendered displayName convention so unzipped frames
    // sort correctly without renaming.
    const ext = extFromUrl(url, '.png')
    assets.push({
      kind: 'frame',
      url,
      filename: `frames/frame_${padded}${ext}`,
      sourceOfTruth: classifySourceOfTruth(url),
    })
  }
  return assets
}

/**
 * Whether the web zip-download button should render for a dataset.
 *
 * - Image datasets: the picture itself.
 * - Frame-mode datasets, even when stored as `video/*`:
 *   `expandFrameAssets()` exposes the per-frame URLs.
 * - `video/*` datasets: the MP4 itself (AYNI's NOAA library).
 *
 * AYNI: TerraViz suppressed plain video rows served through its node
 * catalog's manifest endpoint (HLS-only, nothing to download); that
 * catalog went on 2026-10-06, so no row takes that shape any more.
 * Streams (`application/dash+xml`) and stories still get no button.
 */
export function isZipDownloadable(dataset: Dataset): boolean {
  if (dataset.format.startsWith('image/')) return true
  if (dataset.frames) return true
  return dataset.format.startsWith('video/')
}

/** Format bytes into a human-readable string. */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 1 ? 1 : 0)} ${units[i]}`
}

/**
 * Test-only surface. Exposes the pure helpers used by `resolveAssets`
 * so unit tests call into the real implementation
 * rather than re-running the same logic inline (which would let
 * production drift silently). Don't import this outside `*.test.ts`.
 */
export const __test__ = {
  isHttpUrl,
  extFromUrl,
  classifySourceOfTruth,
  extForContentType,
  filenameForContentType,
}
